import React from 'react'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import BugsnagPerformance from '@bugsnag/browser-performance'

const releaseStage = import.meta.env.VITE_ENV
const isReportingEnabled = ['production', 'staging'].includes(releaseStage)

// Errors we deliberately drop so real bugs aren't buried in noise.
// These are environmental (browser extensions, cross-origin scripts,
// aborted navigations) — not defects in our code.
const NOISE_MESSAGE_PATTERNS = [
  /ResizeObserver loop/i, // benign layout warning
  /^Script error\.?$/i, // cross-origin third-party script, no detail
  /Request aborted/i, // navigation/cancel mid-request
  /\bcanceled\b/i, // aborted fetch/axios
  // Firebase Auth IndexedDB persistence disrupted by the environment
  // (Safari/incognito eviction, multi-tab, or the user clearing site data).
  // Unactionable, no stacktrace; fires even on public pages that boot auth.
  /Indexed Database server lost|Database deleted by request of the user/i,
  // Android in-app WebViews (Instagram/Facebook/TikTok) failing across their
  // own native JS bridge. Nothing on our side is involved.
  /Error invoking postMessage/i,
  // Deliberately NOT filtered by message: "SecurityError: The operation is
  // insecure" (WebKit's blocked-storage error). It white-screened the app
  // once already and safeStorage exists to guard it, so a sourced one is a
  // regression we want to see. The injected-script copies carry no source
  // file and get dropped by the unsourced-stack rule below instead.
]

const EXTENSION_FRAME = /^(chrome|moz|safari(-web)?)-extension:\/\//i

// A frame Bugsnag could not attribute to a source file. Scripts injected by
// the browser itself (Chrome iOS translation, in-app WebView analytics shims)
// are loaded via WKUserScript rather than a URL, so they carry no file at all.
const UNSOURCED_FRAME = (frame) => {
  const file = frame.file || ''
  return !file || file === 'unknown' || file === '[native code]'
}

// Certain noise: the event names its own origin, so dropping it is safe.
// Exported for tests; production callers go through the onError hook below.
export function isNoise(event) {
  const err = event.errors && event.errors[0]
  if (!err) return false

  const message = err.errorMessage || ''
  if (NOISE_MESSAGE_PATTERNS.some((re) => re.test(message))) return true

  // Errors whose every stack frame lives in a browser extension are not ours.
  const frames = err.stacktrace || []
  if (frames.length && frames.every((f) => EXTENSION_FRAME.test(f.file || ''))) {
    return true
  }

  return false
}

// Probable noise: a stack we can't attribute to any file. Our own code resolves
// to a bundle path, so an entirely unsourced stack almost certainly came from a
// script the browser injected — these were ~80% of production events, nearly all
// Chrome on iOS.
//
// Kept separate from isNoise because it is an inference about how engines format
// stacks, not a fact the event states about itself. If an engine ever reported our
// frames without a file, discarding these would hide real bugs *and* hide the fact
// that it was doing so. So they are tagged and kept, and filtered in the dashboard
// instead — which stays checkable against real traffic and can be undone without a
// deploy.
export function isUnsourcedStack(event) {
  const err = event.errors && event.errors[0]
  if (!err) return false

  const frames = err.stacktrace || []
  return frames.length > 0 && frames.every(UNSOURCED_FRAME)
}

let ErrorBoundary = ({ children }) => children

if (isReportingEnabled) {
  Bugsnag.start({
    apiKey: '9d7b105aa9c28cf0115370c08993eddd',
    plugins: [new BugsnagPluginReact()],
    generateAnonymousId: false,
    samplingProbability: 1.0,
    enabledReleaseStages: ['staging', 'production'],
    releaseStage,
    appVersion: import.meta.env.VITE_APP_VERSION,
    autoTrackSessions: false,
    // Return false to discard the event so it never reaches the dashboard.
    onError: (event) => {
      if (isNoise(event)) return false

      // Tagged rather than dropped so these can be filtered out in the dashboard
      // — and so the rule can be re-tuned there, without a deploy, if our own
      // frames ever start showing up among them.
      if (isUnsourcedStack(event)) {
        event.severity = 'info'
        event.addMetadata('noise', { reason: 'unsourced-stack' })
      }
    },
    sourceMapConfig: {
      endpoint: `${import.meta.env.VITE_FRONTEND_URL}/assets/`,
      uploadSources: true,
    },
  })

  BugsnagPerformance.start({
    apiKey: '9d7b105aa9c28cf0115370c08993eddd',
    enabledReleaseStages: ['staging', 'production'],
    releaseStage,
    appVersion: import.meta.env.VITE_APP_VERSION,
  })

  ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React)
}

// Report an error that was caught in code (try/catch, an error boundary's
// componentDidCatch) — the global handlers only see *uncaught* errors, so
// anything we handle ourselves must be reported explicitly. No-ops when
// reporting is disabled so dev doesn't warn about notify-before-start.
export function notifyError(error, onError) {
  if (!isReportingEnabled) return
  Bugsnag.notify(error, onError)
}

export { ErrorBoundary }
